import { useState, useCallback, useEffect, useRef } from 'react';
import UnifiedAuthService, {
  AuthMethod,
  AuthMethodInfo,
  UnifiedAuthRequestParams,
  UnifiedAuthResult,
} from '../libs/UnifiedAuthService';
import { EZIOKProvider } from '../libs/EZIOKService';
import axiosClient from '../libs/axiosClient';

// 글로벌 콜백 함수를 위한 Window 확장 (이미 다른 훅에서 선언됨)
declare global {
  interface Window {
    mobileOKCallback?: (response: string) => void;
    eziokCallback?: (response: string) => void;
  }
}

interface UseUnifiedAuthOptions {
  // 사용자 ID (로그인 상태일 경우, DB 저장용)
  userId?: number;
  // 인증 성공 콜백
  onSuccess?: (result: UnifiedAuthResult) => void;
  // 인증 실패 콜백
  onError?: (error: Error) => void;
  // 인증 취소 콜백
  onCancel?: () => void;
}

interface UseUnifiedAuthReturn {
  // 통합 인증 시작
  startAuth: (params: UnifiedAuthRequestParams) => Promise<void>;
  // 인증 방식 목록 조회
  getAuthMethods: () => Promise<AuthMethodInfo | null>;
  // 간편인증 사업자 목록 조회
  getEZIOKProviders: () => Promise<EZIOKProvider[]>;
  // 로딩 상태
  isLoading: boolean;
  // 에러 상태
  error: Error | null;
  // SDK 로드 상태
  sdkStatus: { mobileOK: boolean; eziok: boolean };
  // 인증 방식 목록
  authMethods: AuthMethodInfo[];
  // 간편인증 사업자 목록
  eziokProviders: EZIOKProvider[];
  // 현재 선택된 인증 방식
  currentAuthMethod: AuthMethod | null;
  // 인증 방식 변경
  setCurrentAuthMethod: (method: AuthMethod) => void;
}

/**
 * 통합 본인인증 커스텀 훅
 * Mobile-OK(휴대폰 인증)과 EZ-IOK(간편인증)을 통합 관리
 *
 * @example
 * const {
 *   startAuth,
 *   getAuthMethods,
 *   getEZIOKProviders,
 *   isLoading,
 *   authMethods,
 *   eziokProviders,
 * } = useUnifiedAuth({
 *   onSuccess: (result) => console.log('인증 성공:', result),
 *   onError: (error) => console.error('인증 실패:', error),
 * });
 *
 * // 휴대폰 인증 시작
 * await startAuth({
 *   authMethod: 'mobile',
 *   resultUrl: `https://api.ondoc.co.kr/func/mobileok/callback`,
 *   usageCode: '01005',
 * });
 *
 * // 간편인증 시작 (통합창)
 * await startAuth({
 *   authMethod: 'easy',
 *   resultUrl: `https://api.ondoc.co.kr/func/eziok/callback`,
 *   authProviderCode: 'kakao',
 * });
 */
export function useUnifiedAuth(options?: UseUnifiedAuthOptions): UseUnifiedAuthReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sdkStatus, setSdkStatus] = useState({ mobileOK: false, eziok: false });
  const [authMethods, setAuthMethods] = useState<AuthMethodInfo[]>([]);
  const [eziokProviders, setEziokProviders] = useState<EZIOKProvider[]>([]);
  const [currentAuthMethod, setCurrentAuthMethod] = useState<AuthMethod | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // SDK 사전 로드 및 글로벌 콜백 함수 등록
  useEffect(() => {
    // SDK 로드
    UnifiedAuthService.loadSDKs()
      .then((status) => setSdkStatus(status))
      .catch((err) => {
        console.error('[useUnifiedAuth] SDK load failed:', err);
      });

    // 디버그: 콜백 함수 등록 확인
    console.log('[UnifiedAuth] Registering global callbacks...');
    console.log('[UnifiedAuth] Before - mobileOKCallback:', typeof window.mobileOKCallback);
    console.log('[UnifiedAuth] Before - eziokCallback:', typeof window.eziokCallback);

    // Mobile-OK 콜백 등록
    window.mobileOKCallback = async (response: string) => {
      console.log('[UnifiedAuth] MobileOK callback received:', response);
      try {
        const data = JSON.parse(response);
        console.log('[UnifiedAuth] Parsed MobileOK data:', data);

        if (data.resultCode === '0000') {
          // mokToken이 있으면 백엔드 /result API 호출하여 DB에 저장
          const mokToken = data.encryptMOKKeyToken || data.mokToken;
          const storedTxId = sessionStorage.getItem('mobileok_txId');

          if (mokToken && storedTxId) {
            console.log('[UnifiedAuth] Calling /result API with mokToken and txId:', storedTxId);

            try {
              const resultResponse = await axiosClient.post('/func/mobileok/result', {
                mokToken,
                txId: storedTxId,
                userId: optionsRef.current?.userId,
              });

              console.log('[UnifiedAuth] MobileOK result response:', resultResponse.data);

              // 세션 정리
              sessionStorage.removeItem('mobileok_txId');

              const resultData = resultResponse.data;

              if (resultData.resultCode === '0000') {
                const result: UnifiedAuthResult = {
                  authMethod: 'mobile',
                  resultCode: resultData.resultCode,
                  resultMsg: resultData.resultMsg || '인증 성공',
                  ci: resultData.ci,
                  di: resultData.di,
                  name: resultData.name,
                  birthDate: resultData.birthDate,
                  gender: resultData.gender,
                  nationalInfo: resultData.nationalInfo,
                  mobileNo: resultData.mobileNo,
                  mobileCo: resultData.mobileCo,
                };
                optionsRef.current?.onSuccess?.(result);
              } else {
                const error = new Error(resultData.resultMsg || '본인확인 결과 조회에 실패했습니다.');
                optionsRef.current?.onError?.(error);
              }
            } catch (apiErr) {
              console.error('[UnifiedAuth] MobileOK result API error:', apiErr);
              // API 호출 실패해도 기존 data로 성공 처리 (DB 저장만 실패)
              const result: UnifiedAuthResult = {
                authMethod: 'mobile',
                resultCode: data.resultCode,
                resultMsg: data.resultMsg || '인증 성공',
                ...data,
              };
              optionsRef.current?.onSuccess?.(result);
            }
          } else {
            // mokToken이 없으면 기존 방식 (SDK 결과 직접 사용)
            const result: UnifiedAuthResult = {
              authMethod: 'mobile',
              resultCode: data.resultCode,
              resultMsg: data.resultMsg || '인증 성공',
              ...data,
            };
            optionsRef.current?.onSuccess?.(result);
          }
        } else {
          const error = new Error(data.resultMsg || '본인확인에 실패했습니다.');
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[UnifiedAuth] MobileOK callback parse error:', err);
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    console.log('[UnifiedAuth] After - mobileOKCallback:', typeof window.mobileOKCallback);

    // EZ-IOK 콜백 등록
    // 콜백 형식: resultCode|keyToken|errorMsg (파이프 구분)
    window.eziokCallback = async (response: string) => {
      console.log('[UnifiedAuth] EZIOK callback received:', response);
      try {
        // 파이프(|) 구분 문자열 파싱
        const parts = response.split('|');
        const resultCode = parts[0];
        const keyToken = parts[1] || '';
        const errorMsg = parts[2] || '';

        console.log('[UnifiedAuth] Parsed EZIOK:', { resultCode, keyToken, errorMsg });

        if (resultCode === '0') {
          // 성공 - keyToken으로 백엔드 /result API 호출하여 실제 사용자 정보 조회
          const storedTxId = sessionStorage.getItem('eziok_txId');
          if (!storedTxId) {
            throw new Error('유효하지 않은 인증 요청입니다. (txId 없음)');
          }

          console.log('[UnifiedAuth] Calling /result API with keyToken and txId:', storedTxId);

          // 백엔드 /result API 호출
          const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.ondoc.co.kr';
          const resultResponse = await axiosClient.post(`${API_BASE}/func/eziok/result`, {
            keyToken,
            txId: storedTxId,
            userId: optionsRef.current?.userId,
          });

          console.log('[UnifiedAuth] EZIOK result response:', resultResponse.data);

          // 세션 정리
          sessionStorage.removeItem('eziok_txId');

          const resultData = resultResponse.data;

          if (resultData.resultCode === '0000') {
            const result: UnifiedAuthResult = {
              authMethod: 'easy',
              resultCode: resultData.resultCode,
              resultMsg: resultData.resultMsg || '인증 성공',
              ci: resultData.ci,
              di: resultData.di,
              name: resultData.name,
              birthDate: resultData.birthDate,
              gender: resultData.gender,
              nationalInfo: resultData.nationalInfo,
              mobileNo: resultData.mobileNo,
              mobileCo: resultData.mobileCo,
            };
            optionsRef.current?.onSuccess?.(result);
          } else {
            const error = new Error(resultData.resultMsg || '간편인증 결과 조회에 실패했습니다.');
            optionsRef.current?.onError?.(error);
          }
        } else {
          const error = new Error(errorMsg || '간편인증에 실패했습니다.');
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[UnifiedAuth] EZIOK callback error:', err);
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    console.log('[UnifiedAuth] After - eziokCallback:', typeof window.eziokCallback);
    console.log('[UnifiedAuth] Callbacks registered successfully');

    return () => {
      delete window.mobileOKCallback;
      delete window.eziokCallback;
    };
  }, []);

  // 인증 방식 목록 조회
  const getAuthMethods = useCallback(async (): Promise<AuthMethodInfo | null> => {
    try {
      const methods = await UnifiedAuthService.getAuthMethods();
      if (methods) {
        setAuthMethods([methods]);
      }
      return methods;
    } catch (err) {
      console.error('[useUnifiedAuth] Failed to get auth methods:', err);
      return null;
    }
  }, []);

  // 간편인증 사업자 목록 조회
  const getEZIOKProviders = useCallback(async (): Promise<EZIOKProvider[]> => {
    try {
      const providers = await UnifiedAuthService.getEZIOKProviders();
      setEziokProviders(providers);
      return providers;
    } catch (err) {
      console.error('[useUnifiedAuth] Failed to get EZIOK providers:', err);
      return [];
    }
  }, []);

  // 통합 인증 시작
  const startAuth = useCallback(
    async (params: UnifiedAuthRequestParams) => {
      console.log('[UnifiedAuth] startAuth called with:', params);
      setIsLoading(true);
      setError(null);
      setCurrentAuthMethod(params.authMethod);

      try {
        await UnifiedAuthService.startAuth(params);
        setIsLoading(false);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('인증 요청 실패');
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
        throw error;
      }
    },
    [options],
  );

  return {
    startAuth,
    getAuthMethods,
    getEZIOKProviders,
    isLoading,
    error,
    sdkStatus,
    authMethods,
    eziokProviders,
    currentAuthMethod,
    setCurrentAuthMethod,
  };
}

export default useUnifiedAuth;